import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Divider,
} from "@mui/material";
import {
  Security,
  CheckCircle,
  VpnKey,
  Login,
  PersonAdd,
  Update,
} from "@mui/icons-material";
import { mobiliTheme } from "../../../../../themes/mobiliTheme";

const TSSInstructions: React.FC = React.memo(() => {
  const steps = [
    {
      icon: <CheckCircle sx={{ fontSize: 18, color: "#1976d2" }} />,
      title: "TSS erstellen",
      description:
        "Klicken Sie auf 'Neue TSS erstellen'. Status wird auf CREATED gesetzt.",
    },
    {
      icon: <Update sx={{ fontSize: 18, color: "#ff9800" }} />,
      title: "Status auf UNINITIALIZED setzen",
      description: "Aktualisieren Sie den TSS-Status (ERFORDERLICH)",
      required: true,
    },
    {
      icon: <VpnKey sx={{ fontSize: 18, color: "#9c27b0" }} />,
      title: "Admin-PIN ändern",
      description: "Legen Sie eine sichere PIN für die TSS fest",
      required: true,
    },
    {
      icon: <Login sx={{ fontSize: 18, color: "#00897b" }} />,
      title: "Als Admin authentifizieren",
      description: "Melden Sie sich mit der PIN vor der Initialisierung an",
      required: true,
    },
    {
      icon: <Update sx={{ fontSize: 18, color: "#4caf50" }} />,
      title: "Status auf INITIALIZED setzen",
      description: "Vor der Client-Erstellung erforderlich",
      required: true,
    },
    {
      icon: <PersonAdd sx={{ fontSize: 18, color: "#1976d2" }} />,
      title: "Client erstellen",
      description: "Ein Client pro TSS für die Transaktionsverarbeitung",
    },
  ];

  return (
    <Card
      sx={{
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        height: "fit-content",
        boxShadow: 3,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              bgcolor: "#1976d2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Security sx={{ color: "#fff", fontSize: 18 }} />
          </Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: "#1a237e",
            }}
          >
            TSS-Einrichtung
          </Typography>
        </Stack>

        <Typography
          variant="caption"
          sx={{
            mb: 2.5,
            display: "block",
            color: "text.secondary",
            lineHeight: 1.6,
          }}
        >
          Um eine TSS und einen Client einzurichten, klicken Sie auf{" "}
          <strong>'Neue TSS'</strong>. Ein Einrichtungsassistent führt Sie
          Schritt für Schritt durch den gesamten Prozess. Folgen Sie einfach den
          Anweisungen im Modal.
        </Typography>
        <Typography
          variant="caption"
          sx={{
            mb: 2.5,
            display: "block",
            color: "text.primary",
            fontWeight: 600,
            lineHeight: 1.6,
          }}
        >
          Falls Sie die genauen Schritte kennen möchten, finden Sie nachfolgend
          den detaillierten Ablauf:
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Stack spacing={2}>
          {steps.map((step, index) => (
            <Box
              key={index}
              sx={{
                p: 1.5,
                borderRadius: 1,
                bgcolor: "rgba(255, 255, 255, 0.8)",
                border: "1px solid",
                borderColor: step.required ? "#ff9800" : "divider",
                transition: "all 0.2s ease",
                "&:hover": {
                  bgcolor: "rgba(255, 255, 255, 1)",
                  boxShadow: 2,
                  transform: "translateX(4px)",
                },
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    bgcolor: "#003059",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: 11,
                    flexShrink: 0,
                  }}
                >
                  {index + 1}
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 0.5 }}
                  >
                    {step.icon}
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.813rem",
                        color: "#1a237e",
                      }}
                    >
                      {step.title}
                    </Typography>
                    {step.required && (
                      <Box
                        component="span"
                        sx={{
                          fontSize: "0.65rem",
                          fontWeight: 700,
                          color: "#fff",
                          bgcolor: "#ff9800",
                          px: 0.75,
                          py: 0.25,
                          borderRadius: 0.5,
                        }}
                      >
                        ERFORDERLICH
                      </Box>
                    )}
                  </Stack>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.75rem",
                      color: "text.secondary",
                      display: "block",
                    }}
                  >
                    {step.description}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
});

TSSInstructions.displayName = "TSSInstructions";

export default TSSInstructions;
